import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Chip,
  MenuItem,
  Autocomplete,
  Link,
  IconButton,
  Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { DataGrid } from "@mui/x-data-grid";
import dayjs from "dayjs";
import api from "../api";
import MonthSummaryBar from "./MonthSummaryBar";
import PageHeader from "./common/PageHeader";
import PageContainer from "./common/PageContainer";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";

export default function PaymentList() {
  const navigate = useNavigate();
  // xlsx 行68: ステータス変更は admin/manager のみ
  const { role } = useAuth();
  const canApprove = role === 'admin' || role === 'manager';
  const [statusEdit, setStatusEdit] = useState({ open: false, id: null, value: '' });

  /* =========================
   * State
   * ========================= */
  const [payments, setPayments] = useState([]);
  const [total, setTotal] = useState(0);

  const [availableMonths, setAvailableMonths] = useState([]);
  const [month, setMonth] = useState("");

  const [outsources, setOutsources] = useState([]);
  const [projects, setProjects] = useState([]);

  const [selectedPayee, setSelectedPayee] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);

  const [open, setOpen] = useState(false);

  const [form, setForm] = useState({
    expenseType: "project", // ★追加
    projectId: null,
    payeeId: null,
    amount: "",
    status: "承認待ち",
    requestedAt: dayjs().format("YYYY-MM-DD"),
    paidAt: "",
    note: "",
  });

  /* =========================
   * Load masters
   * ========================= */
  useEffect(() => {
    (async () => {
      try {
        const [o, p] = await Promise.all([
          api.get("/api/outsources"),
          api.get("/api/projects"),
        ]);

        setOutsources(Array.isArray(o.data) ? o.data : o.data.data || []);
        setProjects(Array.isArray(p.data) ? p.data : []);
      } catch (e) {
        console.error("[load masters]", e);
      }
    })();
  }, []);

  /* =========================
   * Load months
   * ========================= */
  useEffect(() => {
    (async () => {
      const res = await api.get("/api/payments/months");
      const list = Array.isArray(res.data) ? res.data : [];
      setAvailableMonths(list);
      if (list.length > 0) setMonth(list[0]);
    })();
  }, []);

  /* =========================
   * Load payments
   * ========================= */
  const loadPayments = async () => {
    if (!month) return;
    const res = await api.get("/api/payments", { params: { month } });
    setPayments(res.data.data || []);
    setTotal(res.data.totalAmount || 0);
  };

  useEffect(() => {
    loadPayments();
  }, [month]); // eslint-disable-line react-hooks/exhaustive-deps

  /* =========================
   * Submit
   * ========================= */
  const handleSubmit = async () => {
    if (!form.payeeId) {
      alert("支払先（外注先）を選択してください");
      return;
    }

    if (form.expenseType === "project" && !form.projectId) {
      alert("案件原価の場合はプロジェクト選択が必須です");
      return;
    }

    await api.post("/api/payments", {
      ...form,
      projectId: form.expenseType === "project" ? form.projectId : null,
      amount: Number(form.amount),
      paidAt: form.paidAt || null,
    });

    setOpen(false);
    resetForm();
  };

  const resetForm = () => {
    setForm({
      expenseType: "project",
      projectId: null,
      payeeId: null,
      amount: "",
      status: "承認待ち",
      requestedAt: dayjs().format("YYYY-MM-DD"),
      paidAt: "",
      note: "",
    });
    setSelectedPayee(null);
    setSelectedProject(null);
  };

  /* =========================
   * Columns
   * ========================= */
  const columns = [
    { field: "id", headerName: "ID", width: 80 },
    {
      field: "expenseType",
      headerName: "区分",
      width: 110,
      renderCell: (p) =>
        p.value === "general" ? (
          <Chip label="一般経費" size="small" />
        ) : (
          <Chip label="案件原価" color="primary" size="small" />
        ),
    },
    {
      field: "payeeName",
      headerName: "支払先",
      width: 200,
    },
    {
      field: "projectTitle",
      headerName: "案件",
      width: 220,
      renderCell: (p) => p.value || "-",
    },
    {
      field: "amount",
      headerName: "金額",
      width: 120,
      renderCell: (p) => `¥${p.value.toLocaleString()}`,
    },
    {
      field: "status",
      headerName: "ステータス",
      width: 130,
      renderCell: (p) => {
        // xlsx 行27: 8値スキーマの色分類
        const color =
          p.value === '承認済' || p.value === '支払済' ? 'success' :
          p.value === '差し戻し' ? 'error' :
          p.value === '納品済' ? 'warning' :
          p.value === '起案' ? 'default' :
          'info';
        return <Chip label={p.value || '-'} color={color} size="small" />;
      },
    },
    {
      field: "requestedAt",
      headerName: "登録日",
      width: 130,
      renderCell: (p) =>
        p.value ? dayjs(p.value).format("YYYY/MM/DD") : "-",
    },
    ...(canApprove ? [{
      field: "actions",
      headerName: "",
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (p) => (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', height: '100%' }}>
          <Tooltip title="ステータス変更">
            <IconButton size="small" onClick={() => setStatusEdit({ open: true, id: p.row.id, value: p.row.status || '' })}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    }] : []),
  ];

  const handleSaveStatus = async () => {
    try {
      await api.patch(`/api/payments/${statusEdit.id}/status`, { status: statusEdit.value });
      setStatusEdit({ open: false, id: null, value: '' });
      loadPayments();
    } catch (e) {
      alert(`変更できませんでした: ${e.response?.data?.error || e.message}`);
    }
  };

  /* =========================
   * Render
   * ========================= */
  return (
    <PageContainer>
      <PageHeader
        title="外注先支払管理"
        actions={
          <Button variant="contained" onClick={() => setOpen(true)}>
            新規支払登録
          </Button>
        }
      />

      <Box sx={{ display: "flex", gap: 2, mb: 2, alignItems: 'center' }}>
        <TextField
          select
          size="small"
          label="対象月"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          sx={{ minWidth: 160 }}
        >
          {availableMonths.map((m) => (
            <MenuItem key={m} value={m}>
              {dayjs(`${m}-01`).format("YYYY年MM月")}
            </MenuItem>
          ))}
        </TextField>

        <Typography variant="body2">
          合計支払額：<strong>¥{total.toLocaleString()}</strong>
        </Typography>
      </Box>

      <MonthSummaryBar type="payments" month={month} />

      <DataGrid
        rows={payments}
        columns={columns}
        autoHeight
        pageSize={20}
      />

      {/* 登録ダイアログ */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>支払登録</DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            margin="dense"
            label="支払区分"
            value={form.expenseType}
            onChange={(e) =>
              setForm({ ...form, expenseType: e.target.value })
            }
          >
            <MenuItem value="project">案件原価</MenuItem>
            <MenuItem value="general">一般経費</MenuItem>
          </TextField>

          {form.expenseType === "project" && (
            <Autocomplete
              options={projects}
              getOptionLabel={(p) =>
                `${p.customerName} / ${p.title}`
              }
              value={selectedProject}
              onChange={(_, v) => {
                setSelectedProject(v);
                setForm({ ...form, projectId: v?.id || null });
              }}
              renderInput={(params) => (
                <TextField {...params} label="案件（必須）" margin="dense" />
              )}
            />
          )}

          <Autocomplete
            options={outsources}
            getOptionLabel={(o) => o.name}
            value={selectedPayee}
            onChange={(_, v) => {
              setSelectedPayee(v);
              setForm({ ...form, payeeId: v?.id || null });
            }}
            renderInput={(params) => (
              <TextField {...params} label="支払先" margin="dense" />
            )}
          />

          <Box sx={{ mt: 1 }}>
            <Link component="button" onClick={() => navigate("/outsource")}>
              外注先が未登録の場合はこちらから登録
            </Link>
          </Box>

          <TextField
            fullWidth
            margin="dense"
            type="number"
            label="金額"
            value={form.amount}
            onChange={(e) =>
              setForm({ ...form, amount: e.target.value })
            }
          />

          <TextField
            fullWidth
            margin="dense"
            label="備考"
            multiline
            rows={2}
            value={form.note}
            onChange={(e) =>
              setForm({ ...form, note: e.target.value })
            }
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)}>キャンセル</Button>
          <Button variant="contained" onClick={handleSubmit}>
            登録
          </Button>
        </DialogActions>
      </Dialog>

      {/* ステータス変更ダイアログ (admin/manager のみ表示、xlsx 行68) */}
      <Dialog open={statusEdit.open} onClose={() => setStatusEdit({ open: false, id: null, value: '' })} fullWidth maxWidth="xs">
        <DialogTitle>ステータス変更</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            select
            fullWidth
            label="新しいステータス"
            value={statusEdit.value}
            onChange={(e) => setStatusEdit({ ...statusEdit, value: e.target.value })}
            sx={{ mt: 1 }}
          >
            <MenuItem value="起案">起案</MenuItem>
            <MenuItem value="稟議中">稟議中</MenuItem>
            <MenuItem value="発注済">発注済</MenuItem>
            <MenuItem value="差し戻し">差し戻し</MenuItem>
            <MenuItem value="納品済">納品済</MenuItem>
            <MenuItem value="承認済">承認済</MenuItem>
            <MenuItem value="請求書受領">請求書受領</MenuItem>
            <MenuItem value="支払済">支払済</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusEdit({ open: false, id: null, value: '' })}>キャンセル</Button>
          <Button variant="contained" onClick={handleSaveStatus}>保存</Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
}
